import React, { Suspense } from 'react';
import './polyfill.resize-observer';
import type {} from './styled-component-override';
import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { ApolloProvider } from '@apollo/client';
import { QueryClientProvider, QueryClient } from 'react-query';

import { Spinner, SpinnerSize } from '@amzn/atoz-web/Spinner';
import { StencilProvider } from '@amzn/atoz-web/Theme';
import { View } from '@amzn/atoz-web/View';

import {
    TaaConfigsContextProvider,
    TaaConfigs
} from '@amzn/atoz-dashboard-taa-components';

import {
    createApolloClient,
    apiGatewayUrl,
    Region
} from '@amzn/atoz-shiftapp-gql';
import { setupI18nextLanguage } from '@amzn/atoz-shiftapp-localization';
import { logger, METRIC_NAME } from '@amzn/atoz-shiftapp-metrics';
import { ErrorBoundary } from '@amzn/atoz-shiftapp-features';
import {
    AssociateContextProvider,
    BIAnalytics
} from '@amzn/atoz-shiftapp-utilities';

import { getAssociateInfo } from './associateInfo';
import { getFeatures } from './features';
import { setAssociateLocaleAndTimeZone } from './utils';
import { App } from './components/App';
import { GlobalStyleReset } from './components/GlobalStyleReset';
import { appWebTheme } from './consts';
import { useCurrentStage } from '@amzn/atoz-shiftapp-initializer';

import { initialiseDayOne } from '@amzn/atozmobile-tenant-day-one-info/dist/utils/initialiseDayOne';
import { DocumentTitle } from './components/DocumentTitle';

const mountShiftManagementApp = () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const currentStage = useCurrentStage();
    const APP_ROOT = document.getElementById('atoz-shift-management-page-root');
    const associateInfo = getAssociateInfo();
    const features = getFeatures();
    const apiRegion = features.smaApiUsEastEnabled
        ? Region.usEast
        : Region.usWest;
    const client = createApolloClient(
        associateInfo.employeeId,
        currentStage,
        apiRegion
    );
    const taaConfigs: TaaConfigs = {
        clientName: 'SMA',
        host: undefined,
        associateInfo
    };
    setAssociateLocaleAndTimeZone(associateInfo.locale, associateInfo.timeZone);
    const i18n = setupI18nextLanguage(associateInfo.locale);

    if (features.isPreHireIngressWidgetEnabled) {
        initialiseDayOne({
            employeeId: associateInfo.employeeId,
            baseUrl: apiGatewayUrl[apiRegion][currentStage],
            i18n,
            emitClickMetric: BIAnalytics.clickOnDayOneWidget,
            emitErrorMetric: BIAnalytics.errorInDayOneWidget
        });
    }

    ReactDOM.render(
        <React.StrictMode>
            <BrowserRouter>
                <AssociateContextProvider
                    associateInfo={associateInfo}
                    features={features}
                >
                    <ApolloProvider client={client}>
                        <QueryClientProvider client={new QueryClient()}>
                            <GlobalStyleReset />
                            <StencilProvider theme={appWebTheme}>
                                <Suspense
                                    fallback={
                                        <View margin="S300">
                                            <Spinner
                                                dataTestId="SuspenseLoader"
                                                size={SpinnerSize.Medium}
                                            />
                                        </View>
                                    }
                                >
                                    <ErrorBoundary>
                                        <I18nextProvider i18n={i18n}>
                                            <TaaConfigsContextProvider
                                                taaConfigs={taaConfigs}
                                            >
                                                <DocumentTitle />
                                                <App />
                                            </TaaConfigsContextProvider>
                                        </I18nextProvider>
                                    </ErrorBoundary>
                                </Suspense>
                            </StencilProvider>
                        </QueryClientProvider>
                    </ApolloProvider>
                </AssociateContextProvider>
            </BrowserRouter>
        </React.StrictMode>,
        APP_ROOT,
        () => {
            logger.emitCountMetric({
                metricName: METRIC_NAME.APP_MOUNT,
                dimensions: {}
            });
        }
    );
};

logger.reportWebVitals();
mountShiftManagementApp();
